import React from 'react';
import { Truck, X, Package, Scale, MapPin, Plus, Trash2, ArrowBigLeft } from 'lucide-react';
import '../../../../../styles/components/vehicle-register.scss';
import i18n from '../../../../../i18n';
import { toast } from 'react-toastify';
import { getLoggedUser } from '../../../../../state/user';
import {
  createDeliverySchedule,
  getAuctionList,
  getGroupsSelectors,
  getMaterialsList,
  getOrganizationSelectors,
  getUserList,
  getVehiclesList,
} from '../services/LoadingManagementService';
import { formatTons } from '../../../../../utils/common';

class DeliverySchedule extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hideSidebar: false,
      showModal: false,

      // Main Group Information
      client: '', // Organization/Client first
      groupName: '',
      projectCode: '',
      supervisorName: '',
      groupActualName: '',

      // Data lists
      groups: [], // Groups filtered by organization
      clients: [],
      vehicles: [],
      materials: [],
      supervisors: [],
      auctions_data: [],

      // Auction Collections
      auctions: [
        {
          id: 1,
          auctionId: '',
          bos_number: '',
          auctionActualName: '',
          buyerName: '',
          buyerId: '',
          materialType: '',
          expectedWeight: '',
          loadFrom: '',
          loadTo: '',
          yards: '',
          scheduledStartDate: '',
          scheduledEndDate: '',
          auctionSupervisor: '',
          status: 'pending',
          assignedVehicles: [],
          selectedVehicle: '',
        },
      ],

      // Validation
      errors: {},

      // Loading state
      loadingClients: false,
      loadingGroups: false,
      loadingAuction: false,
      loadingVehicles: false,
      loadingMaterial: false,
      loadingSupervisors: false,
    };
  }

  // Add componentDidMount to fetch supervisors when component loads
  componentDidMount() {
    this.fetchMaterials();
    this.fetchGroupsList();
    this.fetchSupervisors();
    this.fetchVehiclesList();
    this.fetchAllOrganization();
  }

  // Handle input changes for main group fields
  handleInputChange = (field, value) => {
    this.setState({
      [field]: value,
      errors: { ...this.state.errors, [field]: '' },
    });
  };

  fetchMaterials = async () => {
    this.setState({ loadingMaterial: true });

    try {
      const filters = {};
      const response = await getMaterialsList(filters);

      if (response.success && response.data) {
        // Transform the data to match your dropdown needs
        const materials = response.data?.map((material) => ({
          id: material.id,
          value: material.id,
          label: `${material.item_code} - ${material.name}`,
        }));

        this.setState({
          materials,
          loadingMaterial: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch materials');
        this.setState({ loadingMaterial: false });
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      toast.error('Error loading materials list');
      this.setState({ loadingMaterial: false });
    }
  };

  // Fetch supervisors from API
  fetchSupervisors = async () => {
    this.setState({ loadingSupervisors: true });

    try {
      const filters = { role: 'O' };
      const response = await getUserList(filters);

      if (response.success && response.data) {
        // Transform the data to match your dropdown needs
        const supervisors = response.data.map((user) => ({
          id: user.id,
          value: user.id,
          label: `${user.name} - ${user.username}`,
          name: user.name,
          username: user.username,
          status: user.status,
        }));

        this.setState({
          supervisors,
          loadingSupervisors: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch supervisors');
        this.setState({ loadingSupervisors: false });
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
      toast.error('Error loading supervisors list');
      this.setState({ loadingSupervisors: false });
    }
  };

  // Fetch auction list from API - Now called when group is selected
  fetchAuctionList = async (groupId) => {
    this.setState({ loadingAuction: true });

    try {
      const filters = { groupId: groupId };
      const response = await getAuctionList(filters);

      if (response.success && response.data) {
        // Transform the data to include winners information
        const auctions_data = response.data.map((auction) => ({
          id: auction.id,
          value: auction.id,
          label: `${auction.title}`,
          name: auction.title,
          location: auction.location,
          winners: auction.winners || [], // This will always have one winner
        }));

        this.setState({
          auctions_data,
          loadingAuction: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch auction');
        this.setState({ loadingAuction: false });
      }
    } catch (error) {
      console.error('Error fetching auction:', error);
      toast.error('Error loading auction list');
      this.setState({ loadingAuction: false });
    }
  };

  // Fetch vehicles list from API
  fetchVehiclesList = async () => {
    this.setState({ loadingVehicles: true });

    try {
      const filters = {};
      const response = await getVehiclesList(filters);

      if (response.success && response.data) {
        // Transform the data to match your dropdown needs
        const vehicles = response.data.map((vehicle) => ({
          id: vehicle.id,
          value: vehicle.id,
          label: `${vehicle.vehicle_number} - ${vehicle.vehicle_model}`,
          vehicle_number: vehicle.vehicle_number,
          vehicle_model: vehicle.vehicle_model,
          driver_name: vehicle.driver_name || 'No Driver',
          status: vehicle.status,
        }));

        this.setState({
          vehicles,
          loadingVehicles: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch vehicles');
        this.setState({ loadingVehicles: false });
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Error loading vehicles list');
      this.setState({ loadingVehicles: false });
    }
  };

  fetchAllOrganization = async () => {
    this.setState({ loadingClients: true });

    try {
      const response = await getOrganizationSelectors();
      if (response.success && response.data) {
        const clients = response.data.map((client) => ({
          id: client.id,
          value: client.id,
          label: client.organization_name,
          organization_name: client.organization_name,
        }));

        this.setState({
          clients,
          loadingClients: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch organization');
        this.setState({ loadingClients: false });
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast.error('Error loading organization list');
      this.setState({ loadingClients: false });
    }
  };

  // Fetch groups filtered by organization
  fetchGroupsList = async (organizationId) => {
    this.setState({ loadingGroups: true });

    try {
      const filters = {
        organization_id: organizationId,
        delivery_status: 7,
      };

      const response = await getGroupsSelectors(filters);

      if (response.success && response.data) {
        const groups = response.data.map((group) => ({
          id: group.id,
          value: group.id,
          label: group.group_name,
          name: group.group_name,
          project_code: group.project_code,
          organization_id: group.organization_id,
        }));

        this.setState({
          groups,
          loadingGroups: false,
        });
      } else {
        toast.error(response.message || 'Failed to fetch groups');
        this.setState({ loadingGroups: false, groups: [] });
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Error loading groups list');
      this.setState({
        loadingGroups: false,
        groups: [],
        groupName: '',
        groupActualName: '',
      });
    }
  };

  handleClientChange = (e) => {
    const clientId = e.target.value;
    const selectedClient = this.state.clients.find((c) => c.id == clientId);

    this.setState((prevState) => ({
      client: clientId,
      groups: [], // Clear groups when client changes
      groupName: '',
      groupActualName: '',
      auctions_data: [], // Clear auctions data
      errors: {
        ...prevState.errors,
        client: '',
        groupName: '',
        groupActualName: '',
      },
    }));

    // Fetch groups for the selected organization
    if (clientId) {
      this.fetchGroupsList(clientId);
      if (selectedClient) {
        toast.success(`Client "${selectedClient.organization_name}" selected. Loading groups...`);
      }
    }
  };

  handleGroupChange = (e) => {
    const groupId = e.target.value;
    const selectedGroup = this.state.groups.find((g) => g.id == groupId);

    this.setState((prevState) => ({
      groupName: groupId,
      groupActualName: selectedGroup ? selectedGroup.label : '', // Auto-fill with the selected label
      projectCode: selectedGroup ? selectedGroup.project_code : '', // Auto-fill with the selected label
      errors: {
        ...prevState.errors,
        groupName: '',
        groupActualName: '',
      },
      auctions_data: [], // Clear previous auctions
    }));

    // Fetch auctions for the selected group
    if (groupId) {
      this.fetchAuctionList(groupId);
      if (selectedGroup) {
        toast.success(`Group "${selectedGroup.label}" selected. Loading auctions...`);
      }
    }
  };

  // Handle auction selection change - auto-populate all fields
  handleAuctionSelectionChange = (index, auctionId) => {
    const selectedAuction = this.state.auctions_data.find(
      (auction) => auction.id.toString() === auctionId
    );

    if (selectedAuction) {
      // Auto-populate all fields from the selected auction
      const winner =
        selectedAuction.winners && selectedAuction.winners.length > 0
          ? selectedAuction.winners[0]
          : null;

      const updatedAuctions = [...this.state.auctions];
      updatedAuctions[index].auctionId = auctionId;
      updatedAuctions[index].auctionActualName = selectedAuction.label;
      updatedAuctions[index].loadFrom = selectedAuction.location;
      updatedAuctions[index].buyerName = winner ? winner.user.name : '';
      updatedAuctions[index].buyerId = winner ? winner.user_id : '';

      this.setState({
        auctions: updatedAuctions,
        errors: {
          ...this.state.errors,
          [`auction_${index}_id`]: '',
          [`auction_${index}_name`]: '',
        },
      });
    }
  };

  // Handle auction input changes
  handleAuctionChange = (index, field, value) => {
    const updatedAuctions = [...this.state.auctions];
    updatedAuctions[index][field] = value;

    this.setState({
      auctions: updatedAuctions,
      errors: { ...this.state.errors, [`auction_${index}_${field}`]: '' },
    });
  };

  // Add vehicle to specific auction
  addVehicleToAuction = (auctionIndex) => {
    const { auctions, vehicles } = this.state;
    const auction = auctions[auctionIndex];

    if (!auction.selectedVehicle) {
      toast.error('Please select a vehicle first');
      return;
    }

    // Check if vehicle is already assigned to this auction
    const isAlreadyAssigned = auction.assignedVehicles.some((v) => v.id == auction.selectedVehicle);

    if (isAlreadyAssigned) {
      toast.error('This vehicle is already assigned to this auction');
      return;
    }

    // Find the selected vehicle details
    const selectedVehicleData = vehicles.find((v) => v.id == auction.selectedVehicle);

    if (!selectedVehicleData) {
      toast.error('Selected vehicle not found');
      return;
    }

    // Add vehicle to auction's assigned vehicles
    const updatedAuctions = [...auctions];
    updatedAuctions[auctionIndex].assignedVehicles = [
      ...updatedAuctions[auctionIndex].assignedVehicles,
      {
        id: selectedVehicleData.id,
        vehicle_number: selectedVehicleData.vehicle_number,
        vehicle_model: selectedVehicleData.vehicle_model,
        driver_name: selectedVehicleData.driver_name,
        label: selectedVehicleData.label,
      },
    ];

    // Clear the selected vehicle dropdown
    updatedAuctions[auctionIndex].selectedVehicle = '';

    this.setState({
      auctions: updatedAuctions,
      errors: {
        ...this.state.errors,
        [`auction_${auctionIndex}_vehicles`]: '',
      },
    });
  };

  // Remove vehicle from specific auction
  removeVehicleFromAuction = (auctionIndex, vehicleId) => {
    const updatedAuctions = [...this.state.auctions];
    updatedAuctions[auctionIndex].assignedVehicles = updatedAuctions[
      auctionIndex
    ].assignedVehicles.filter((vehicle) => vehicle.id !== vehicleId);

    this.setState({ auctions: updatedAuctions });
  };

  // Add new auction
  addAuction = () => {
    this.setState((prevState) => ({
      auctions: [
        ...prevState.auctions,
        {
          id: Date.now(),
          auctionId: '',
          bos_number: '',
          auctionActualName: '',
          buyerName: '',
          buyerId: '',
          materialType: '',
          expectedWeight: '',
          loadFrom: '',
          loadTo: '',
          scheduledStartDate: '',
          scheduledEndDate: '',
          auctionSupervisor: '',
          status: 'pending',
          assignedVehicles: [],
          selectedVehicle: '',
        },
      ],
    }));
  };

  // Remove auction
  removeAuction = (index) => {
    if (this.state.auctions.length > 1) {
      const updatedAuctions = this.state.auctions.filter((_, i) => i !== index);
      this.setState({ auctions: updatedAuctions });
    }
  };

  // Validation function
  validateForm = () => {
    const errors = {};

    // Validate main group fields
    if (!this.state.groupName) errors.groupName = 'Group name is required';
    if (!this.state.groupActualName) errors.groupActualName = 'Group actual name is required';
    if (!this.state.supervisorName) errors.supervisorName = 'Supervisor name is required';

    // Validate each auction
    this.state.auctions.forEach((auction, index) => {
      if (!auction.auctionId) errors[`auction_${index}_id`] = 'Auction ID is required';
      if (!auction.bos_number) errors[`auction_${index}_bos_number`] = 'Bos Number is required';
      if (!auction.auctionActualName) errors[`auction_${index}_name`] = 'Auction name is required';
      if (!auction.buyerName) errors[`auction_${index}_buyer`] = 'Buyer name is required';
      if (!auction.materialType) errors[`auction_${index}_material`] = 'Material type is required';
      if (!auction.yards) errors[`auction_${index}_yards`] = 'Yards Count is required';
      if (!auction.expectedWeight || auction.expectedWeight <= 0) {
        errors[`auction_${index}_weight`] = 'Valid expected weight is required';
      }
      if (!auction.loadFrom) errors[`auction_${index}_loadFrom`] = 'Load from location is required';
      if (!auction.loadTo) errors[`auction_${index}_loadTo`] = 'Load to location is required';
      if (!auction.scheduledStartDate)
        errors[`auction_${index}_startDate`] = 'Start date is required';
      if (!auction.scheduledEndDate) errors[`auction_${index}_endDate`] = 'End date is required';

      // Validate at least one vehicle is assigned to this auction
      if (auction.assignedVehicles.length === 0) {
        errors[`auction_${index}_vehicles`] =
          'At least one vehicle must be assigned to this auction';
      }

      // Validate date range
      if (auction.scheduledStartDate && auction.scheduledEndDate) {
        const startDate = new Date(auction.scheduledStartDate);
        const endDate = new Date(auction.scheduledEndDate);
        if (endDate < startDate) {
          errors[`auction_${index}_dateRange`] = 'End date cannot be before start date';
        }
      }
    });

    return errors;
  };

  // Add these helper methods to your class (above handleSave):

  getMaterialName = (materialId) => {
    const { materials } = this.state;
    const material = materials.find((m) => m.id == materialId);
    if (!material) return '';

    // Extract name from label (format: "item_code - name")
    const parts = material.label.split(' - ');
    return parts.length > 1 ? parts[1] : material.label;
  };

  getSupervisorName = (supervisorId) => {
    const { supervisors } = this.state;
    const supervisor = supervisors.find((s) => s.id == supervisorId);
    return supervisor ? supervisor.name : '';
  };

  handleSave = async () => {
    const errors = this.validateForm();

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    // Get logged user info
    const user = getLoggedUser();

    // Prepare the payload object matching your API format
    const payload = {
      project_name: this.state.groupActualName,
      supervisor_id: this.state.supervisorName,
      group_id: this.state.groupName,
      delivery_sites: [],
    };

    // Add delivery sites information with their respective vehicle IDs
    this.state.auctions.forEach((auction, index) => {
      const deliverySite = {
        buyer_name: auction.buyerName,
        lot_ref: auction.auctionId,
        bos_number: auction.bos_number,
        material_type: this.getMaterialName(auction.materialType),
        yards: auction.yards,
        expected_weight: auction.expectedWeight,
        load_from: auction.loadFrom,
        load_to: auction.loadTo,
        expecting_start_date: auction.scheduledStartDate,
        expecting_end_date: auction.scheduledEndDate,
        auditor_name: auction.auditorName || '', // Add logic to get auditor name
        auction_id: auction.auctionId,
        area_id: 1,
        auction_name: auction.auctionActualName,
        buyer_id: auction.buyerId || '',
        material_id: auction.materialType,
        supervisor_name: this.getSupervisorName(this.state.supervisorName),
        supervisor_id: this.state.supervisorName,
      };

      // Add vehicle_ids to each delivery site if there are assigned vehicles
      if (auction.assignedVehicles && auction.assignedVehicles.length > 0) {
        deliverySite.vehicle_ids = auction.assignedVehicles.map((vehicle) => vehicle.id);
      }

      payload.delivery_sites.push(deliverySite);
    });

    try {
      // Call the API with JSON payload
      const response = await createDeliverySchedule(payload, false);

      if (response.success) {
        toast.success('Delivery schedule created successfully!');
        // Reset form or redirect
        // this.resetForm();
      } else {
        toast.error(response.message || 'Failed to create schedule');
      }
    } catch (error) {
      console.error('Error creating delivery schedule:', error);
      toast.error('Error creating delivery schedule. Please try again.');
    }
  };

  render() {
    const {
      client,
      groupName,
      groupActualName,
      projectCode,
      supervisorName,
      auctions,
      auctions_data,
      errors,
      clients,
      groups,
      vehicles,
      materials,
      supervisors,
      loadingClients,
      loadingGroups,
      loadingVehicles,
      loadingMaterial,
      loadingAuction,
      loadingSupervisors,
    } = this.state;

    const user = getLoggedUser();

    return (
      <React.Fragment>
        <main className="main-content card">
          <div className="page-header">
            <button className="button1 button1-primary" onClick={() => window.history.back()}>
              <ArrowBigLeft /> {i18n.t('dashboard')}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h1>{i18n.t('delivery_schedule')}</h1>
            </div>

            <div></div>
          </div>

          <div className="content-grid">
            <div className="right-column">
              {/* Schedule Summary */}
              <section className="card-t1">
                <div className="card-t1-header section-divider">
                  <Package size={24} className="header-icon" />
                  <h2>Schedule Summary</h2>
                </div>
                <div className="schedule-summary">
                  <div className="summary-item">
                    <span className="label">Total Auctions:</span>
                    <span className="value">{auctions.length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Total Vehicles Assigned:</span>
                    <span className="value">
                      {auctions.reduce(
                        (total, auction) => total + auction.assignedVehicles.length,
                        0
                      )}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Total Expected Weight:</span>
                    <span className="value">
                      {auctions
                        .reduce(
                          (total, auction) => total + (parseFloat(auction.expectedWeight) || 0),
                          0
                        )
                        .toFixed(2)}{' '}
                      kg
                    </span>
                  </div>

                  <div className="summary-item">
                    <span className="label">Total Expected Weight:</span>
                    <span className="value">
                      {auctions.reduce(
                        (total, auction) => total + (formatTons(auction.expectedWeight) || 0),
                        0
                      )}{' '}
                      TONS
                    </span>
                  </div>
                </div>
              </section>
            </div>

            {/* Left Column - Group & Auctions */}
            <section className="card-t1 vehicle-details">
              {/* Main Group Section */}
              <div className="form-grid">
                <div className="form-group full-width section-divider">
                  <div className="card-t1-header">
                    <MapPin size={24} className="header-icon" />
                    <h2>{i18n.t('main_site')}</h2>
                  </div>
                </div>

                <div className="form-group" style={{ width: '220px' }}>
                  <label className="required">{i18n.t('organization')}</label>
                  <select
                    value={client}
                    onChange={this.handleClientChange}
                    className={errors.client ? 'error' : ''}
                    disabled={loadingClients}
                  >
                    <option value="">Select Client</option>
                    {loadingClients ? (
                      <option value="" disabled>
                        Loading Clients...
                      </option>
                    ) : (
                      clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.label}
                        </option>
                      ))
                    )}
                  </select>
                  {errors.client && <span className="error-message">{errors.client}</span>}
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={this.fetchAllOrganization}
                    style={{
                      marginTop: '5px',
                      fontSize: '12px',
                      padding: '2px 8px',
                      textAlign: 'center',
                    }}
                  >
                    ↻ Refresh Clients
                  </button>
                </div>

                {/* Group Selection (Enabled only after client is selected) */}
                <div className="form-group" style={{ width: '220px' }}>
                  <label className="required">{i18n.t('project_name')}</label>
                  <select
                    value={groupName}
                    onChange={this.handleGroupChange}
                    className={errors.groupName ? 'error' : ''}
                    disabled={loadingGroups || !client}
                  >
                    <option value="">Select Group</option>
                    {!client ? (
                      <option value="" disabled>
                        Select a client first
                      </option>
                    ) : loadingGroups ? (
                      <option value="" disabled>
                        Loading Groups...
                      </option>
                    ) : groups.length === 0 ? (
                      <option value="" disabled>
                        No groups found for this client
                      </option>
                    ) : (
                      groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.label}
                        </option>
                      ))
                    )}
                  </select>
                  {errors.groupName && <span className="error-message">{errors.groupName}</span>}
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={() => client && this.fetchGroupsList(client)}
                    style={{
                      marginTop: '5px',
                      fontSize: '12px',
                      padding: '2px 8px',
                      textAlign: 'center',
                    }}
                    disabled={!client}
                  >
                    ↻ Refresh Groups
                  </button>
                </div>

                <div className="form-group" style={{ width: '220px' }}>
                  <label className="required">{i18n.t('project_actual_name')}</label>
                  <input
                    type="text"
                    value={groupActualName}
                    onChange={(e) => this.handleInputChange('groupActualName', e.target.value)}
                    placeholder="Enter group name"
                    className={errors.groupActualName ? 'error' : ''}
                  />
                  {errors.groupActualName && (
                    <span className="error-message">{errors.groupActualName}</span>
                  )}
                </div>

                <div className="form-group" style={{ width: '220px' }}>
                  <label className="required">{i18n.t('project_code')}</label>
                  <input type="text" value={projectCode} />
                </div>

                {/* Other fields remain the same... */}
                <div className="form-group" style={{ width: '220px' }}>
                  <label className="required">{i18n.t('supervisor_name')}</label>
                  <select
                    value={supervisorName}
                    onChange={(e) => this.handleInputChange('supervisorName', e.target.value)}
                    className={errors.supervisorName ? 'error' : ''}
                    disabled={loadingSupervisors}
                  >
                    <option value="">Select Supervisor</option>
                    {loadingSupervisors ? (
                      <option value="" disabled>
                        Loading supervisors...
                      </option>
                    ) : (
                      supervisors.map((supervisor) => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.label}
                        </option>
                      ))
                    )}
                  </select>
                  {errors.supervisorName && (
                    <span className="error-message">{errors.supervisorName}</span>
                  )}
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={this.fetchSupervisors}
                    style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px' }}
                  >
                    ↻ Refresh List
                  </button>
                </div>
              </div>

              {/* Auctions Section */}
              <div className="auctions-section">
                <div className="section-header section-divider">
                  <div className="card-t1-header">
                    <Scale size={24} className="header-icon" />
                    <h2>{i18n.t('auction_sites')}</h2>
                  </div>
                  <button
                    type="button"
                    className="button1 button1-primary"
                    onClick={this.addAuction}
                  >
                    <Plus size={16} />
                    Add Auction Site
                  </button>
                </div>

                <div className="auctions-list">
                  {auctions.map((auction, index) => (
                    <div key={auction.id} className="auction-card">
                      <div className="auction-header">
                        <h3>
                          Auction Site #{index + 1} (MZAD {auction.auctionId})
                        </h3>
                        {auctions.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove-auction"
                            onClick={() => this.removeAuction(index)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <div className="form-grid">
                        <div className="form-group">
                          <label className="required">{i18n.t('bos_number')}</label>
                          <input
                            type="text"
                            value={auction.bos_number}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'bos_number', e.target.value)
                            }
                            placeholder="Enter Bos Number"
                            className={errors[`auction_${index}_bos_number`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_bos_number`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_bos_number`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group" style={{ width: '220px' }}>
                          <label className="required">{i18n.t('auction_name')}</label>
                          <select
                            id={`auction-select-${index}`}
                            value={auction.auctionId}
                            onChange={(e) =>
                              this.handleAuctionSelectionChange(index, e.target.value)
                            }
                            className={errors[`auction_${index}_id`] ? 'error' : ''}
                            disabled={loadingAuction || !groupName}
                          >
                            <option value="">Select Auction</option>
                            {loadingAuction ? (
                              <option value="" disabled>
                                Loading auctions...
                              </option>
                            ) : !groupName ? (
                              <option value="" disabled>
                                Select a group first
                              </option>
                            ) : auctions_data.length === 0 ? (
                              <option value="" disabled>
                                No auctions found for this group
                              </option>
                            ) : (
                              auctions_data.map((auctionItem) => {
                                const winner =
                                  auctionItem.winners && auctionItem.winners.length > 0
                                    ? auctionItem.winners[0].user?.name || 'No Name'
                                    : 'No Winner';

                                return (
                                  <option key={auctionItem.id} value={auctionItem.id}>
                                    {auctionItem.label} (Winner: {winner})
                                  </option>
                                );
                              })
                            )}
                          </select>
                          {errors[`auction_${index}_id`] && (
                            <span className="error-message">{errors[`auction_${index}_id`]}</span>
                          )}
                          <button
                            type="button"
                            className="button1 button1-primary"
                            onClick={() => groupName && this.fetchAuctionList(groupName)}
                            style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px' }}
                            disabled={!groupName}
                          >
                            ↻ Refresh List
                          </button>
                        </div>

                        <div className="form-group" style={{ width: '220px' }}>
                          <label className="required">{i18n.t('auction_actual_name')}</label>
                          <input
                            type="text"
                            value={auction.auctionActualName}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'auctionActualName', e.target.value)
                            }
                            placeholder="Enter auction name"
                            className={errors[`auction_${index}_name`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_name`] && (
                            <span className="error-message">{errors[`auction_${index}_name`]}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="required">Buyer Name</label>
                          <input
                            type="text"
                            value={auction.buyerName}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'buyerName', e.target.value)
                            }
                            placeholder="Enter buyer name"
                            className={errors[`auction_${index}_buyer`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_buyer`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_buyer`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="required">{i18n.t('yards')}</label>
                          <input
                            type="number"
                            value={auction.yards}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'yards', e.target.value)
                            }
                            placeholder="How Many Yards"
                            className={errors[`auction_${index}_yards`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_yards`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_yards`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group" style={{ width: '220px' }}>
                          <label className="required">{i18n.t('material_type')}</label>
                          <select
                            value={auction.materialType}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'materialType', e.target.value)
                            }
                            className={errors[`auction_${index}_material`] ? 'error' : ''}
                            disabled={loadingMaterial}
                          >
                            <option value="">Select Material Type</option>
                            {loadingMaterial ? (
                              <option value="" disabled>
                                Loading Material...
                              </option>
                            ) : (
                              materials.map((material) => (
                                <option key={material.id} value={material.id}>
                                  {material.label}
                                </option>
                              ))
                            )}
                          </select>
                          {errors[`auction_${index}_material`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_material`]}
                            </span>
                          )}
                          <button
                            type="button"
                            className="button1 button1-primary"
                            onClick={this.fetchMaterials}
                            style={{ marginTop: '5px', fontSize: '12px', padding: '2px 8px' }}
                          >
                            ↻ Refresh List
                          </button>
                        </div>

                        <div className="form-group">
                          <label className="required">Expected Weight (KG)</label>
                          <input
                            type="number"
                            value={auction.expectedWeight}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'expectedWeight', e.target.value)
                            }
                            placeholder="Enter expected weight"
                            min="0"
                            step="0.01"
                            className={errors[`auction_${index}_weight`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_weight`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_weight`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="required">Load From</label>
                          <input
                            type="text"
                            value={auction.loadFrom}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'loadFrom', e.target.value)
                            }
                            placeholder="Loading location"
                            className={errors[`auction_${index}_loadFrom`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_loadFrom`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_loadFrom`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="required">Load To</label>
                          <input
                            type="text"
                            value={auction.loadTo}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'loadTo', e.target.value)
                            }
                            placeholder="Destination location"
                            className={errors[`auction_${index}_loadTo`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_loadTo`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_loadTo`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="required">Expecting Start Date</label>
                          <input
                            type="date"
                            value={auction.scheduledStartDate}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'scheduledStartDate', e.target.value)
                            }
                            className={errors[`auction_${index}_startDate`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_startDate`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_startDate`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="required">Expecting End Date</label>
                          <input
                            type="date"
                            value={auction.scheduledEndDate}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'scheduledEndDate', e.target.value)
                            }
                            className={errors[`auction_${index}_endDate`] ? 'error' : ''}
                          />
                          {errors[`auction_${index}_endDate`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_endDate`]}
                            </span>
                          )}
                        </div>

                        {errors[`auction_${index}_dateRange`] && (
                          <div className="form-group full-width">
                            <span className="error-message">
                              {errors[`auction_${index}_dateRange`]}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Vehicle Assignment per Auction */}
                      <div className="auction-header pt-5">
                        <div className="card-t1-header">
                          <Truck size={24} className="header-icon" />
                          <h3>Vehicle Assignment for Auction #{index + 1}</h3>
                        </div>
                      </div>

                      <div className="form-grid">
                        <div className="form-group" style={{ width: '220px' }}>
                          <label className="required">Select Vehicle</label>
                          <select
                            value={auction.selectedVehicle}
                            onChange={(e) =>
                              this.handleAuctionChange(index, 'selectedVehicle', e.target.value)
                            }
                            className={errors[`auction_${index}_vehicles`] ? 'error' : ''}
                            disabled={loadingVehicles}
                          >
                            <option value="">Select Vehicle</option>
                            {loadingVehicles ? (
                              <option value="" disabled>
                                Loading vehicles...
                              </option>
                            ) : (
                              vehicles.map((vehicle) => (
                                <option key={vehicle.id} value={vehicle.id}>
                                  {vehicle.label} (Driver: {vehicle.driver_name})
                                </option>
                              ))
                            )}
                          </select>
                          {errors[`auction_${index}_vehicles`] && (
                            <span className="error-message">
                              {errors[`auction_${index}_vehicles`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>&nbsp;</label>
                          <button
                            type="button"
                            className="button1 button1-primary"
                            onClick={() => this.addVehicleToAuction(index)}
                          >
                            <Plus size={16} /> Add Vehicle
                          </button>
                        </div>
                      </div>

                      <div className="assigned-vehicles-section">
                        <h3 className="section-title section-divider">
                          <Truck size={20} />
                          Assigned Vehicles for Auction #{index + 1}
                          <span className="vehicles-count">
                            ({auction.assignedVehicles.length})
                          </span>
                        </h3>

                        {auction.assignedVehicles.length === 0 ? (
                          <div className="no-vehicles-message">
                            No vehicles assigned yet. Please add vehicles above.
                          </div>
                        ) : (
                          <div className="vehicles-list">
                            {auction.assignedVehicles.map((vehicle) => (
                              <div key={vehicle.id} className="vehicle-card">
                                <button
                                  className="remove-vehicle"
                                  onClick={() => this.removeVehicleFromAuction(index, vehicle.id)}
                                >
                                  <X size={16} />
                                </button>
                                <div className="vehicle-icon">
                                  <Truck size={24} />
                                </div>
                                <div className="vehicle-info">
                                  <h4>{vehicle.vehicle_number}</h4>
                                  <p>{vehicle.vehicle_model}</p>
                                  <p className="driver-info">Driver: {vehicle.driver_name}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="page-footer">
                <div className="footer-actions">
                  <button className="button1 button1-secondary">{i18n.t('cancel')}</button>
                  <button className="button1 button1-primary" onClick={() => this.handleSave()}>
                    {i18n.t('submit')}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </React.Fragment>
    );
  }
}

export default DeliverySchedule;
